import {
  Button,
  Checkbox,
  CheckboxOnChangeData,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  makeStyles,
  Textarea,
  TextareaOnChangeData,
} from "@fluentui/react-components";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useSaveAndPublishImageDraft,
  usePublishImage,
} from "../../hooks/useImages";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../ButtonWithLoading";
import { ClientRoutes } from "../../models/ClientRoutes";
import { PublishImageRequest } from "../../models/Image";

const useStyles = makeStyles({
  dialogSurface: {
    width: "400px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
  commentTextArea: {
    minHeight: "120px",
    marginTop: "8px",
  },
  commentLabel: {
    marginTop: "16px",
  },
});

interface ImagePendingUpdate {
  title: string;
  fileName: string;
  isActive: boolean;
  isArchived: boolean;
  newFile: File | null;
}

interface ImagePublishDialogProps {
  versionId: string;
  imageId: string;
  disabled?: boolean;
  /** The current form data to save, in the same request that publishes the draft. */
  pendingUpdate: ImagePendingUpdate;
  /** Increment this value to open the dialog programmatically, e.g. after a combined save-and-publish action. */
  openSignal?: number;
  onPublishStart?: () => void;
  onPublishFailed?: () => void;
  onPublishSuccess?: () => void;
  publishOnly?: boolean;
}

export const ImagePublishDialog = (props: ImagePublishDialogProps) => {
  const {
    versionId,
    imageId,
    disabled,
    pendingUpdate,
    openSignal,
    onPublishStart,
    onPublishFailed,
    onPublishSuccess,
    publishOnly,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const classes = useStyles();

  const { mutateAsync: saveAndPublishImage, isPending: isSaveAndPublishing } =
    useSaveAndPublishImageDraft(versionId, imageId);

  const { mutateAsync: publishImage, isPending: isPublishing } =
    usePublishImage(versionId, imageId);

  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState<string>("");
  const [hiddenComment, setHiddenComment] = useState<boolean>(false);

  // Open the dialog whenever the parent requests it via `openSignal`,
  // e.g. after a combined save-and-publish action.
  const [lastOpenSignal, setLastOpenSignal] = useState(openSignal);
  if (openSignal !== lastOpenSignal) {
    setLastOpenSignal(openSignal);
    if (openSignal) {
      setOpen(true);
    }
  }

  const onCommentChange = (
    _ev: ChangeEvent<HTMLTextAreaElement>,
    data: TextareaOnChangeData
  ) => setComment(data.value);

  const onHiddenChange = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: CheckboxOnChangeData
  ) => setHiddenComment(data.checked as boolean);

  const onPublish = async () => {
    if (!comment) return;

    const publishImageRequest: PublishImageRequest = {
      versionId: versionId,
      comment: comment,
      hiddenComment: hiddenComment,
    };

    onPublishStart?.();

    try {
      await publishImage(publishImageRequest);
      onPublishSuccess?.();
      setOpen(false);
      navigate(ClientRoutes.Media);
    } catch (error) {
      onPublishFailed?.();
      throw error;
    }
  };

  const onSaveAndPublish = async () => {
    if (!comment) return;

    const formDataToSend = new FormData();
    formDataToSend.append("VersionId", versionId);
    formDataToSend.append("Title", pendingUpdate.title);
    formDataToSend.append("FileName", pendingUpdate.fileName);
    formDataToSend.append("IsActive", String(pendingUpdate.isActive));
    formDataToSend.append("IsArchived", String(pendingUpdate.isArchived));
    if (pendingUpdate.newFile) {
      formDataToSend.append("NewImageFile", pendingUpdate.newFile);
    }
    formDataToSend.append("Comment", comment);
    formDataToSend.append("HiddenComment", String(hiddenComment));

    onPublishStart?.();

    try {
      await saveAndPublishImage(formDataToSend);
      onPublishSuccess?.();
      setOpen(false);
      navigate(ClientRoutes.Media);
    } catch (error) {
      onPublishFailed?.();
      throw error;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button
          size="large"
          appearance="primary"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {t("publish")}
        </Button>
      </DialogTrigger>

      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle>{t("publishDraftImageDialogTitle")}</DialogTitle>
          <DialogContent>
            <div className={classes.content}>
              <div>{t("publishDraftImageDescription")}</div>
              <Label className={classes.commentLabel}>
                {t("commentLabel")}
              </Label>
              <Textarea
                value={comment}
                onChange={onCommentChange}
                textarea={{ className: classes.commentTextArea }}
              />
              <Checkbox
                label={t("hideRevisionCheckboxLabel")}
                checked={hiddenComment}
                onChange={onHiddenChange}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpen(false)}
              appearance="secondary"
              disabled={isSaveAndPublishing || isPublishing}
            >
              {t("close")}
            </Button>
            <ButtonWithLoading
              onClick={publishOnly ? onPublish : onSaveAndPublish}
              disabled={!comment}
              isLoading={isSaveAndPublishing || isPublishing}
              text={t("publish")}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
